import api from "../api";
import Country from "../models/Country";
import Region from "../models/Region";
import City from "../models/City";
import CityType from "../models/CityType";
import language from "../utils/language";

/**
 * Обёртка над sessionStorage, добавляющая функционал кольцевого буфера.
 * @template T
 */
class SessionStorageRingBuffer {
  /** 
   * @param {string} key
   * @param {number} capacity
   * @param {Function} fetchFn
   * @param {Function} idSelectorFn
   */
  constructor(key, capacity, fetchFn, idSelectorFn) {
    /** @type {string} */
    this.key = key; // Уникальный ключ для этого типа сущностей
    /** @type {number} */
    this.capacity = capacity; // Максимальное кол-во сущностей, которое может храниться в этом кольцевом буфере
    /** @type {Function} */
    this.fetchFn = fetchFn; // Функция для получения отсутствующих сущностей с бэкенда
    /** @type {Function} */
    this.idSelectorFn = idSelectorFn; // Функция для получения id сущности.

    // читаем один раз при инициализации
    this.data = this._load();

    // Синхронизация сессии между вкладками без лишней десериализации на каждый вызов
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      this._handleStorage = (event) => {
        try {
          if (event.storageArea === sessionStorage && event.key === this.key) {
            this.data = event.newValue ? JSON.parse(event.newValue) || {} : {};
          }
        } catch {
          this.data = {};
        }
      };
      window.addEventListener("storage", this._handleStorage);
    }
  }

  _load() {
    try {
      return JSON.parse(sessionStorage.getItem(this.key)) || {};
    } catch {
      return {};
    }
  }

  _save() {
    sessionStorage.setItem(this.key, JSON.stringify(this.data));
  }

  _ensureLang(lang) {
    if (!this.data[lang]) {
      this.data[lang] = { map: {}, order: [] };
    }
    return this.data[lang];
  }

  /**
   * @param ids {number[]}
   * @returns {Promise<T[]>}
   */
  async getByIds(ids) {
    const lng = language.getLanguage();
    const langData = this._ensureLang(lng);
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));

    /** @type {Record<number, T>} */
    const idToEntity = {};
    const missing = [];

    for (const id of uniqueIds) {
      if (langData.map.hasOwnProperty(id)) {
        idToEntity[id] = langData.map[id];
      } else {
        missing.push(id);
      }
    }

    let hasChanges = false;

    if (missing.length > 0) {
      try {
        const fetched = await this.fetchFn(missing);
        for (const entity of fetched) {
          const entityId = this.idSelectorFn(entity);

          const isNew = !langData.map.hasOwnProperty(entityId);
          langData.map[entityId] = entity;
          if (isNew) {
            langData.order.push(entityId);
            hasChanges = true;
          }

          idToEntity[entityId] = entity;
        }

        // Дедупликация order на случай конкурентных вызовов
        const beforeLength = langData.order.length;
        langData.order = Array.from(new Set(langData.order));
        if (langData.order.length !== beforeLength) {
          hasChanges = true;
        }

        // Пакетное выселение сверх capacity
        while (langData.order.length > this.capacity) {
          const oldId = langData.order.shift();
          delete langData.map[oldId];
          hasChanges = true;
        }

        if (hasChanges) {
          this._save();
        }
      } catch (err) {
        console.error(
          `Ошибка при загрузке сущностей [${missing.join(", ")}], lang=${lng}:`,
          err
        );
        // вернём только то, что было в кеше
      }
    }

    // Гарантируем порядок, соответствующий ids
    return uniqueIds
      .map(id => idToEntity[id] || langData.map[id])
      .filter(Boolean);
  }
}

class GeoStorages {
  constructor() {
    /** @type {SessionStorageRingBuffer<City>} */
    this.cities = new SessionStorageRingBuffer(`cities`, 500, this._fetchCities.bind(this), c => c.id);
    /** @type {SessionStorageRingBuffer<CityType>} */
    this.cityTypes = new SessionStorageRingBuffer(`city_types`, 50, this._fetchCityTypes.bind(this), c => c.id);
    /** @type {SessionStorageRingBuffer<Region>} */
    this.regions = new SessionStorageRingBuffer(`regions`, 100, this._fetchRegions.bind(this), r => r.id);
    /** @type {SessionStorageRingBuffer<Country>} */
    this.countries = new SessionStorageRingBuffer(`countries`, 50, this._fetchCountries.bind(this), c => c.id);
  }

  /** @param {number[]} ids
   * @returns {Promise<City[]>}
   * @private
   */
  async _fetchCities(ids) {
    const response = await api.geo.getCities(ids);
    return Promise.all(response.data.cities.map(c => this._convertCity(c)));
  }

  /** @param {number[]} ids
   * @returns {Promise<CityType[]>}
   * @private
   */
  async _fetchCityTypes(ids) {
    const response = await api.geo.getCityTypes(ids);
    return Promise.all(response.data.cityTypes.map(c => this._convertCityType(c)));
  }

  /** @param {number[]} ids
   * @returns {Promise<Region[]>}
   * @private
   */
  async _fetchRegions(ids) {
    const response = await api.geo.getRegions(ids);
    return Promise.all(response.data.regions.map(r => this._convertRegion(r)));
  }

  /** @param {number[]} ids
   * @returns {Promise<Country[]>}
   * @private
   */
  async _fetchCountries(ids) {
    const response = await api.geo.getCountries(ids);
    return Promise.all(response.data.countries.map(c => this._convertCountry(c)));
  }

  /**
   * @param city
   * @returns {Promise<City>}
   * @private
   */
  async _convertCity(city) {
    const [regionArr, countryArr, cityTypeArr] = await Promise.all([
      city.regionId ? geoStorages.regions.getByIds([city.regionId]) : Promise.resolve([undefined]),
      geoStorages.countries.getByIds([city.countryId]),
      geoStorages.cityTypes.getByIds([city.cityTypeId])
    ]);

    /** @type {Region | undefined} */
    const region = regionArr ? regionArr[0] : undefined;
    /** @type {Country} */
    const country = countryArr[0];
    /** @type {CityType} */
    const cityType = cityTypeArr[0];

    return new City({
      id: city.cityId,
      name: city.name,
      clarifiedName: city.clarifiedName,
      region,
      country,
      geoPoint: city.geoPoint,
      isRegionalCenter: city.isRegionalCenter || false,
      cityType,
      size: city.size
    });
  }

  /** @param cityType
   * @returns {Promise<CityType>}
   * @private
   */
  async _convertCityType(cityType) {
    return new CityType({ id: cityType.cityTypeId, name: cityType.name, shortName: cityType.shortName });
  }
  
  /**
   * @param region
   * @returns {Promise<Region>}
   * @private
   */
  async _convertRegion(region) {
    const country = (await geoStorages.countries.getByIds([region.countryId]))[0];
    return new Region({ id: region.regionId, name: region.name, clarifiedName: region.clarifiedName, country });
  }

  /**
   * @param country
   * @returns {Promise<Country>}
   * @private
   */
  async _convertCountry(country) {
    return new Country({ id: country.countryId, name: country.name });
  }
}

const geoStorages = new GeoStorages();
export default geoStorages;
